import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  CurrencyDisplay,
  DataTable,
  DateDisplay,
  ErrorDisplay,
  Feedback,
  IDataTableColumn,
  useFormEvents,
} from '@lib/common';
import { FormStatus } from '@lib/state';
import { Box, Button, Grid, Tooltip, Typography } from '@material-ui/core';
import { InvoiceRunForms, invoiceRunService, InvoiceStatus, invoiceStatusService } from 'app/state';
import React, { useCallback, useMemo } from 'react';
import { SetInvoiceSentForm } from './set-invoice-sent-form';

interface Props {
  runId: string;
  isDraft: boolean;
  invoices: Array<InvoiceStatus>;
}

interface Account {
  name: string;
  invoices: number;
  total: number;
  recipients?: Array<string>;
  sent?: Date;
  hasRecipients: boolean;
  doNotSend: boolean;
  shouldSend: boolean;
  statuses?: Array<string>;
}

export function SendInvoicesForm({ runId, isDraft, invoices }: Props) {
  const [updateRecipientsOpen, setUpdateRecipientsOpen] = React.useState<boolean>(false);
  const [selectedAccounts, setSelectedAccounts] = React.useState<Array<string>>([]);
  const [selectedInvoices, setSelectedInvoices] = React.useState<Array<string>>([]);
  const [selectedStatuses, setSelectedStatuses] = React.useState<Array<string>>([]);
  const accounts = useMemo(() => {
    const accounts = Array.from(new Set(invoices.map(i => i.corporateAccountId)));

    const mappedAccounts: Array<Account> = accounts.map(account => {
      const accountInvoices = invoices.filter(i => i.corporateAccountId === account);

      const invoicesSent = accountInvoices.map(i =>
        i.invoiceSent ? new Date(i.invoiceSent).getTime() : 0
      );
      const latestDate = Math.max(...invoicesSent);

      return {
        name: accountInvoices[0].corporateAccount ?? '',
        invoices: accountInvoices.length,
        total: accountInvoices.reduce((sum, current) => sum + (current.invoiceTotal ?? 0), 0),
        recipients: accountInvoices[0].recipients ?? undefined,
        sent: latestDate !== 0 ? new Date(latestDate) : undefined,
        hasRecipients: accountInvoices.every(i => i.hasRecipient),
        doNotSend: accountInvoices.every(i => i.doNotSend),
        shouldSend: accountInvoices.some(i => i.shouldGenerateExport),
        statuses: accountInvoices
          .map(i => i.id)
          .filter(id => id !== undefined)
          .map(id => id!),
      };
    });

    return mappedAccounts.sort((a, b) =>
      a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase())
    );
  }, [invoices]);

  const [formEvents, resetFormUI] = useFormEvents(InvoiceRunForms.SendInvoices);

  const onSelectionChange = useCallback(
    (accounts: Array<string>) => {
      setSelectedAccounts(accounts);

      const invoiceIds = invoices
        .filter(i => i.corporateAccount && accounts.includes(i.corporateAccount))
        .map(i => i.invoiceId ?? '');

      setSelectedInvoices(invoiceIds);
    },
    [invoices, setSelectedInvoices]
  );

  const onSubmit = useCallback(() => {
    invoiceRunService.sendInvoices(runId, selectedInvoices);
  }, [runId, selectedInvoices]);

  const onAddRecipients = useCallback(
    (statuses: Array<string>) => {
      setSelectedStatuses(statuses);
      setUpdateRecipientsOpen(true);
    },
    [setSelectedStatuses, setUpdateRecipientsOpen]
  );

  const onSetRecipients = useCallback(
    (recipients: Array<string> | undefined, dateSent: string, replaceRecipients: boolean) => {
      invoiceStatusService.markInvoiceSent(runId, {
        invoiceStatusIds: selectedStatuses,
        dateSent,
        recipients,
        replaceRecipients,
      });
    },
    [runId, selectedStatuses]
  );

  const columns = useMemo(() => getColumns(onAddRecipients, isDraft), [onAddRecipients, isDraft]);

  //Select all invoices to send on load
  // useEffect(() => {
  //     const invoiceIds = invoices.map(i => i.id!);

  //     setSelectedInvoices(invoiceIds);
  //     setSelectedAcccounts(accounts.map(a => a.Name));
  // }, [invoices, accounts, setSelectedInvoices, setSelectedAcccounts]);

  return (
    <DataTable
      items={accounts}
      columns={columns}
      getItemKey={i => i.name}
      isRowSelectable={a => !isDraft && a.hasRecipients && !a.doNotSend && !a.sent && a.shouldSend}
      selectionModel={selectedAccounts}
      onSelectionModelChange={model => onSelectionChange(model.map(x => x as string))}
      checkboxSelection={!isDraft}
    >
      <Grid container item xs={12}>
        <SetInvoiceSentForm
          onSubmit={onSetRecipients}
          open={updateRecipientsOpen}
          onClose={() => setUpdateRecipientsOpen(false)}
        />
        <Box py={1}>
          <Feedback
            show={formEvents.status === FormStatus.Success}
            severity="success"
            onHide={resetFormUI}
            data-testid="invoiceGroupingSuccessDisplay"
          >
            Invoices sent
          </Feedback>
          <ErrorDisplay error={formEvents.error} />
        </Box>
        <Grid container style={{ padding: '1em' }}>
          <Button
            type="button"
            variant="contained"
            color="primary"
            onClick={onSubmit}
            disabled={
              selectedInvoices.length === 0 || isDraft || formEvents.status === FormStatus.Pending
            }
            style={{ marginRight: '1em', display: isDraft ? 'none' : 'block' }}
          >
            Send Invoices
          </Button>
        </Grid>
      </Grid>
    </DataTable>
  );
}

function renderCell(value?: string | null | number) {
  return <Typography>{value}</Typography>;
}

const getColumns = (
  onAddRecipients: (statuses: Array<string>) => void,
  isDraft: boolean
): Array<IDataTableColumn<Account>> => [
  {
    title: 'Corporate Account',
    valueFactory: account => (
      <Tooltip
        title={
          account.hasRecipients
            ? ''
            : 'Sending is disabled because no invoice recipient is set up for this account.'
        }
        placement="bottom-start"
      >
        <Grid container spacing={1}>
          <Grid item xs={1}>
            {!account.hasRecipients && <FontAwesomeIcon icon="info-circle" />}
          </Grid>
          <Grid item xs={11}>
            <Typography>{`${account.name}\t`}</Typography>
          </Grid>
        </Grid>
      </Tooltip>
    ),
  },
  {
    title: 'Invoices Created',
    valueFactory: account => renderCell(account.invoices),
  },
  {
    title: 'Total',
    valueFactory: account => <CurrencyDisplay value={account.total} />,
  },
  {
    title: 'Recipients',
    valueFactory: account => renderCell(account.recipients?.join(', ')),
  },
  {
    title: 'Last Sent',
    valueFactory: account => (
      <>{account.sent && <DateDisplay date={account.sent} dateFormat="MM/dd/yyyy" />}</>
    ),
  },
  {
    title: 'Actions',
    valueFactory: account => (
      <Button
        color="secondary"
        variant="contained"
        onClick={() => onAddRecipients(account.statuses || [])}
        disabled={account.invoices === 0}
        style={{ display: isDraft ? 'none' : 'block' }}
      >
        Already Sent?
      </Button>
    ),
  },
];
